import type { Quaternion } from '../../fisica/trajetoria'
import { subtrair, type Vetor3 } from '../geometria/vetor3'

export type TonusOcupante = 'alerta' | 'inconsciente'

export interface NivelEstresseCervical {
  grau: number
  classificacao: 'fisiologico' | 'atencao' | 'critico'
  rotulo: string
  descricao: string
  condutaCbmmg: string
}

export interface LeituraBiomecanicaInstante {
  anguloCervicalGraus: number
  nivelCervical: NivelEstresseCervical
  estresseJoelhos: 'normal' | 'sobrecarga' | 'luxacaoProvavel'
  estresseCotovelos: 'normal' | 'sobrecarga' | 'luxacaoProvavel'
}

/**
 * Parâmetros de rigidez e amortecimento postural (motores do Rapier)
 * para a coluna (pelve-tronco) e pescoço (tronco-cabeça).
 */
export const CONFIG_TONUS = {
  alerta: {
    stiffnessColuna: 45.0,
    dampingColuna: 8.0,
    // a musculatura cervical acordada segura a cabeça contra a gravidade depois
    // da flexão; com 22/4,5 ela tombava até ~150° e terminava sobre o peito
    stiffnessPescoco: 800.0,
    dampingPescoco: 80.0,
    stiffnessMembros: 18.0,
    dampingMembros: 3.5,
    maxTorqueCervical: 120.0, // Nm aproximado tolerável antes de ceder
    maxTorqueMembros: 90.0,
  },
  inconsciente: {
    // Tônus flácido: apenas a resistência viscoelástica passiva dos ligamentos
    stiffnessColuna: 3.5,
    dampingColuna: 1.2,
    // sem tônus a cabeça cai sobre o peito até o queixo encostar (contato
    // cabeça-tórax no solver); com 1,5 o motor quase não resistia e a flexão
    // passava de 150°
    stiffnessPescoco: 90.0,
    dampingPescoco: 11.0,
    stiffnessMembros: 1.0,
    dampingMembros: 0.4,
    maxTorqueCervical: 35.0,
    maxTorqueMembros: 25.0,
  },
} as const

/** Limiares anatômicos da articulação cervical em graus */
export const LIMIARES_CERVICAIS = {
  limiteFisiologico: 42, // até 42° de flexão/extensão é movimento fisiológico comum
  limiteAtencao: 58,     // 42° a 58°: estiramento de ligamentos anteriores/posteriores (chicote)
  limiteCriticoTrm: 68,  // acima de 68°: hiperflexão/hiperextensão violenta com risco de fratura/luxação e TRM
} as const

/**
 * Calcula o ângulo em radianos e graus entre as orientações do tronco e da cabeça.
 *
 * Utiliza o produto escalar entre quaterniões:
 *   cos(theta / 2) = dot(q1, q2)
 */
export function calcularAnguloCervical(
  rotTronco: Quaternion,
  rotCabeca: Quaternion,
): { rad: number; graus: number } {
  // Garantir menor arco
  const dot = Math.abs(
    rotTronco.x * rotCabeca.x +
      rotTronco.y * rotCabeca.y +
      rotTronco.z * rotCabeca.z +
      rotTronco.w * rotCabeca.w,
  )
  const clampedDot = Math.min(Math.max(dot, 0), 1)
  const rad = 2 * Math.acos(clampedDot)
  const graus = (rad * 180) / Math.PI
  return { rad, graus }
}

/**
 * Avalia o nível de estresse cervical com base no ângulo medido e retorna
 * a classificação e a conduta recomendada do CBMMG.
 */
export function avaliarEstresseCervical(anguloGraus: number): NivelEstresseCervical {
  if (anguloGraus <= LIMIARES_CERVICAIS.limiteFisiologico) {
    return {
      grau: Math.round(anguloGraus),
      classificacao: 'fisiologico',
      rotulo: 'Amplitude fisiológica',
      descricao: 'Deslocamento cervical dentro da amplitude ligamentar segura.',
      condutaCbmmg: 'Manter monitoramento de cinemática e estabilização preventiva.',
    }
  }

  if (anguloGraus <= LIMIARES_CERVICAIS.limiteCriticoTrm) {
    return {
      grau: Math.round(anguloGraus),
      classificacao: 'atencao',
      rotulo: 'Efeito chicote (sobrecarga)',
      descricao: 'Hiperextensão ou hiperflexão com estiramento ligamentar cervical significativo.',
      condutaCbmmg: 'Indicação mandatória de restrição de movimento da coluna (RMC) e colar cervical.',
    }
  }

  return {
    grau: Math.round(anguloGraus),
    classificacao: 'critico',
    rotulo: 'Risco crítico de TRM',
    descricao: 'Força violenta além dos limites ósseos e cápsulo-ligamentares com alto risco de trauma raquimedular.',
    condutaCbmmg: 'Imobilização alinhada em ângulo zero, estabilização manual contínua e extração rápida em prancha longa.',
  }
}

